import { lazy, Suspense, useEffect } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { LoginPage } from '../features/auth/LoginPage'
import { AuthLayout } from '../features/auth/AuthLayout'
import { ProtectedRoute } from '../features/auth/ProtectedRoute'
import { ConversationsPage } from '../features/conversations/ConversationsPage'
import { AutomatedActivityPage } from '../features/automated-activity/AutomatedActivityPage'
import { OverviewPage } from '../features/overview/OverviewPage'
import { AppointmentsPage } from '../features/appointments/AppointmentsPage'
import { CalendarPage } from '../features/calendar/CalendarPage'
import { PatientsPage } from '../features/patients/PatientsPage'
import { PatientDetailPage } from '../features/patients/PatientDetailPage'
import { SettingsPage } from '../features/settings/SettingsPage'
import { ReportIssuePage } from '../features/issue-report/ReportIssuePage'
import { OnboardingLayout } from '../features/onboarding/OnboardingLayout'
import { OnboardingPage } from '../features/onboarding/OnboardingPage'
import { NexHealthConnectionPage } from '../features/onboarding/NexHealthConnectionPage'
import { NexHealthLocationPage } from '../features/onboarding/NexHealthLocationPage'
import { ProfilePage } from '../features/onboarding/ProfilePage'
import { ProvisioningPage } from '../features/onboarding/ProvisioningPage'
import { ReadyClinicGate } from '../features/onboarding/ReadyClinicGate'
import { ReviewPage } from '../features/onboarding/ReviewPage'
import { SchedulingBookingPage } from '../features/onboarding/SchedulingBookingPage'
import { PmsOptionalPage } from '../features/onboarding/PmsOptionalPage'
import { SchedulingCheckPage } from '../features/onboarding/SchedulingCheckPage'
import { SignupPage } from '../features/onboarding/SignupPage'
import { VerifyEmailPage } from '../features/onboarding/VerifyEmailPage'
import { CommunicationsPage } from '../features/onboarding/CommunicationsPage'
import { OrganizationClinicOnboardingPage } from '../features/onboarding/OrganizationClinicOnboardingPage'
import { OrganizationCommunicationsSetupPage } from '../features/onboarding/OrganizationCommunicationsSetupPage'
import { ManagerInvitationClaimPage } from '../features/onboarding/ManagerInvitationClaimPage'
import { OrganizationAdminRoute } from '../features/onboarding/OrganizationAdminRoute'
import { DashboardLayout } from './DashboardLayout'

// Analytics is its own chunk, kept out of the bundle every other page loads.
// It is fetched while the browser is idle, so opening it rarely waits.
const loadAnalytics = () => import('../features/analytics/AnalyticsPage')
const AnalyticsPage = lazy(() =>
  loadAnalytics().then((module) => ({ default: module.AnalyticsPage }))
)

function usePrefetchWhenIdle(load: () => Promise<unknown>) {
  useEffect(() => {
    const prefetch = () => void load().catch(() => undefined)
    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(prefetch, { timeout: 4000 })
      return () => window.cancelIdleCallback(handle)
    }
    const handle = setTimeout(prefetch, 2000)
    return () => clearTimeout(handle)
  }, [load])
}

function PageLoading() {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label="Loading"
      style={{ minHeight: '60vh' }}
    />
  )
}

export function AppRoutes() {
  usePrefetchWhenIdle(loadAnalytics)
  return (
    <Routes>
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route
          path="/manager-invitations/claim"
          element={<ManagerInvitationClaimPage />}
        />
      </Route>
      <Route element={<ProtectedRoute />}>
        <Route path="onboarding" element={<OnboardingLayout />}>
          <Route index element={<OnboardingPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="nexhealth" element={<NexHealthConnectionPage />} />
          <Route path="without-pms" element={<PmsOptionalPage />} />
          <Route
            path="nexhealth/location"
            element={<NexHealthLocationPage />}
          />
          <Route path="verify-email" element={<VerifyEmailPage />} />
          <Route path="scheduling" element={<SchedulingCheckPage />} />
          <Route path="communications" element={<CommunicationsPage />} />
          <Route path="review" element={<ReviewPage />} />
          <Route path="provisioning" element={<ProvisioningPage />} />
          <Route
            path="scheduling/booking"
            element={<SchedulingBookingPage />}
          />
        </Route>
        <Route element={<ReadyClinicGate />}>
          <Route element={<DashboardLayout />}>
            <Route index element={<OverviewPage />} />
            <Route path="conversations" element={<ConversationsPage />} />
            <Route
              path="automated-activity"
              element={<AutomatedActivityPage />}
            />
            <Route path="patients" element={<PatientsPage />} />
            <Route
              path="patients/:patientAuthorityId"
              element={<PatientDetailPage />}
            />
            <Route path="appointments" element={<AppointmentsPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route
              path="analytics"
              element={
                <Suspense fallback={<PageLoading />}>
                  <AnalyticsPage />
                </Suspense>
              }
            />
            <Route path="settings" element={<SettingsPage />} />
            <Route element={<OrganizationAdminRoute />}>
              <Route
                path="organization/clinics/add"
                element={<OrganizationClinicOnboardingPage />}
              />
              <Route
                path="organization/clinics/:tenantUid/communications"
                element={<OrganizationCommunicationsSetupPage />}
              />
            </Route>
            <Route path="report-issue" element={<ReportIssuePage />} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
